import { t } from '../strings';

/**
 * Global search (piece 12). The panel is a query over what the viewer is allowed
 * to see, not an assumed constant: a group with zero matches is omitted
 * entirely, never shown empty.
 */

export const SEARCH_MIN_CHARS = 2;
export const SEARCH_RESULTS_PER_GROUP = 5;

/**
 * The nine groups results fall into; the panel was sized from the first eight. 🔒 Settings is the ninth, and it is
 * drawn only when the module chooser names it (decision 2770).
 */
export const SEARCH_GROUPS = ['Companies', 'People', 'Deals', 'Documents', 'Locations', 'Items', 'Assets', 'Knowledge Base', 'Settings'] as const;
export type SearchGroup = (typeof SEARCH_GROUPS)[number];

/** 🔒 The record groups the records search answers, each one a module the chooser offers (decision 2770). */
export const RECORD_SEARCH_GROUPS = ['Companies', 'People', 'Deals', 'Locations', 'Items'] as const;
export type RecordSearchGroup = (typeof RECORD_SEARCH_GROUPS)[number];

export function isRecordSearchGroup(value: unknown): value is RecordSearchGroup {
  return typeof value === 'string' && (RECORD_SEARCH_GROUPS as readonly string[]).includes(value);
}

/**
 * 🔒 The top bar's module chooser (decision 2770; T1616): All, the record groups the search answers, the
 * knowledge base and Settings, in the panel's own order. 🔨 Documents and Assets are not offered while the search
 * answers neither, because a module that can only answer nothing is a dead choice.
 */
export const SEARCH_MODULES = ['All', ...RECORD_SEARCH_GROUPS, 'Knowledge Base', 'Settings'] as const;
export type SearchModule = (typeof SEARCH_MODULES)[number];
export const SEARCH_ALL: SearchModule = 'All';

/** Which of the three searches a module asks. */
export interface SearchSources {
  /** The records search, and the one group it is narrowed to, or null for every group. */
  records: { group: RecordSearchGroup | null } | null;
  knowledge: boolean;
  settings: boolean;
}

/**
 * 🔒 All asks the records and the knowledge base and never Settings, the owner's cost concern of 2026-10-08; a
 * record module asks the records search for its own group alone; Settings asks no server search at all.
 */
export function searchSources(module: SearchModule): SearchSources {
  if (module === 'All') return { records: { group: null }, knowledge: true, settings: false };
  if (module === 'Knowledge Base') return { records: null, knowledge: true, settings: false };
  if (module === 'Settings') return { records: null, knowledge: false, settings: true };
  return { records: { group: module }, knowledge: false, settings: false };
}

/** The Index's own address with a term in its search box, where the Settings group's See all lands (decision 2770). */
export const SETTINGS_SEARCH_TERM_PARAM = 'q';
export function settingsSearchHref(term: string): string {
  return `/settings?${SETTINGS_SEARCH_TERM_PARAM}=${encodeURIComponent(term.trim())}`;
}

/**
 * The six document types that consolidate into one Documents group, each row
 * carrying its own document-type Pill — never one group per document type, the
 * same convention the Pending Approvals queue uses. 🔨 A hit carries the stored
 * kind and the pill reads its name off the one map (decision 0638), so search
 * holds no copy of the names of its own.
 */
export const DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'Invoice', 'PurchaseOrder', 'Bill', 'WorkOrder'] as const;
export type SearchDocumentType = (typeof DOCUMENT_TYPES)[number];

export interface SearchHit {
  id: string;
  group: SearchGroup;
  label: string;
  route: string;
  /** The document-type Pill a Documents row carries, and nothing else does. */
  documentType?: SearchDocumentType;
  /** The custom field a value was found in, indexed within its record's own group (decision 0233). */
  matchedField?: string;
  /** 🔨 A knowledge-base hit's ranked passage, `[[` and `]]` around each match, drawn as the row's second line (piece 30, Search). */
  passage?: string;
  /** 🔒 T3028, T3066: a person or company hit whose latest checks hold an open suggestion, which draws the gold dot beside the name (piece 13). */
  suggestions?: boolean;
}

export interface SearchGroupResult {
  group: SearchGroup;
  rows: readonly SearchHit[];
  /** The full match count, so a See all N link can name it. */
  total: number;
  /** The list page the See all link opens, pre-filtered through the New View Modal's criteria. */
  seeAllRoute: string | null;
}

export function searchIsReady(term: string): boolean {
  return term.trim().length >= SEARCH_MIN_CHARS;
}

/** A group with zero matches is omitted; a group over the cap keeps five rows and names its total. */
export function groupResults(hits: readonly SearchHit[], totals: ReadonlyMap<SearchGroup, number>, seeAllRoutes: ReadonlyMap<SearchGroup, string>): SearchGroupResult[] {
  const out: SearchGroupResult[] = [];
  for (const group of SEARCH_GROUPS) {
    const rows = hits.filter((h) => h.group === group).slice(0, SEARCH_RESULTS_PER_GROUP);
    if (rows.length === 0) continue;
    out.push({ group, rows, total: totals.get(group) ?? rows.length, seeAllRoute: seeAllRoutes.get(group) ?? null });
  }
  return out;
}

/** The See all link's own text; it names the total even when every row is shown, so the reader knows what is behind it. */
export function seeAllLabel(result: SearchGroupResult): string {
  return `${t('shell.searchSeeAll')} ${result.total}`;
}

/**
 * 🔨 T3332: Enter in the box with no row highlighted opens the first group's See all route, the group the
 * module menu chose first, so a search that matched five or fewer rows still opens its filtered list.
 */
export function searchEnterRoute(results: readonly SearchGroupResult[], module: SearchModule): string | null {
  const chosen = results.find((r) => r.group === module && r.seeAllRoute);
  return (chosen ?? results.find((r) => r.seeAllRoute))?.seeAllRoute ?? null;
}
